import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { unwrapResult } from '@reduxjs/toolkit';
import { IconButton } from '@mui/material';
import { ReactComponent as CloseIcon } from '../../assets/images/svg/close.svg';
import BellIcon from '../../assets/images/emptyContentPushes.png';
import CustomDrawer from '../common/CustomDrawer';
import './PushNotifications.scss';
import { useDebounce } from '../../helpers/hooks/commonHooks';
import Notification from './Notification';
import NotificationSkeleton from '../common/NotificationSkeleton';
import { getIsOpenedPushNotifications, markNotificationAsRead } from '../../../features/user/userSlice';

const PushNotifications = ({
  pushNotifications,
  isNotificationsLoading,
  nextPageUrl,
  getMessages,
  handleClose,
  setIsNewMessages
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isOpenPushNotifications = useSelector(getIsOpenedPushNotifications);
  const [seenNotificationsId, setSeenNotificationsId] = useState([]);
  const [isRendered, setIsRendered] = useState(false);
  const [loadingPage, setLoadingPage] = useState(0);
  const bottomRef = useRef(null);
  const containerRef = useRef(null);
  const notificationsParent = useRef(null);
  const seenNotificationsDates = seenNotificationsId.map((seenId) =>
    pushNotifications.find((p) => Number(p.id) === Number(seenId))?.created_at
  );
  const untilDate =
    seenNotificationsDates.length ? seenNotificationsDates.reduce((min, curr) => curr < min ? curr : min) : '';
  const newestNotificationId = pushNotifications[0]?.id;
  const markAsReadNotifications = useDebounce(
    useCallback(async () => {
      if (untilDate.length) {
        try {
          await unwrapResult(await dispatch(markNotificationAsRead({ until: null })));
          setSeenNotificationsId([]);
        } catch (err) {
          console.log(err);
        }
      }
    }, [untilDate, dispatch]),
    1000,
  );

  const getCreateDate = (date) => {
    const chunkedDate = date.split(' ');
    chunkedDate[0] = chunkedDate[0].replace(/-/g, '/');
    const newDate = new Date(chunkedDate.join(' '));
    return new Date(newDate.toLocaleString('en-US', { timeZone: 'Europe/Kiev' }));
  };

  const getFormatDate = (date) => {
    if (date) {
      const day = date.getUTCDate();
      const month = date.getUTCMonth() + 1;
      const year = date.getUTCFullYear();

      return `${day} ${month} ${year}`;
    }
    return '';
  };

  const validateCreateNotificationDate = (notifications) => {
    let prevDate = null;
    return notifications.map((el) => {
      const createDate = el.created_at ? getCreateDate(el.created_at) : null;
      if (getFormatDate(createDate) !== getFormatDate(prevDate)) {
        prevDate = createDate;
        return { ...el, createdAt: el.created_at };
      }
      return { ...el, created_at: null, createdAt: el.created_at };
    });
  };

  const findFirstUnreadNotification = (notifications) => {
    if (notifications) {
      const reversedArray = [...notifications].reverse();
      return reversedArray.find((el) => el.read_at === null);
    }
    return null;
  };

  const onDrawerScroll = useCallback((e) => {
    const el = e.target;
    if (el.scrollTop < 850 && nextPageUrl) {
      const params = new URLSearchParams(new URL(nextPageUrl).search);
      const page = Number(params.get('page'));
      if (notificationsParent.current?.childElementCount === 20 * (page - 1)) {
        setLoadingPage(page);
        getMessages(`${page}`);
      }
    }
  }, [nextPageUrl, getMessages]);

  const handleCloseDrawer = () => {
    if (containerRef.current) {
      containerRef.current.parentElement?.removeEventListener('scroll', onDrawerScroll);
    }
    setLoadingPage(0);
    handleClose();
  };

  useEffect(() => {
    const rootEl = containerRef.current?.parentElement;

    if (rootEl && nextPageUrl) {
      rootEl.addEventListener('scroll', onDrawerScroll);
    }
    return () => {
      rootEl?.removeEventListener('scroll', onDrawerScroll);
    };
  }, [nextPageUrl, containerRef.current?.parentElement, onDrawerScroll]);

  useEffect(() => {
    if (notificationsParent.current?.childElementCount) {
      const index = notificationsParent.current.childElementCount % 20 || 20;
      notificationsParent.current?.children[index]?.scrollIntoView({ block: 'center' });
    }
  }, [pushNotifications?.length]);

  useEffect(() => {
    setIsNewMessages(!!findFirstUnreadNotification(pushNotifications));
  }, [pushNotifications, setIsNewMessages]);

  useEffect(() => {
    setIsRendered(prev => !prev);
  }, [isOpenPushNotifications]);

  useEffect(() => {
    if (!loadingPage) bottomRef.current?.scrollIntoView();
  }, [isRendered, newestNotificationId, loadingPage]);

  useEffect(() => {
    markAsReadNotifications();
  }, [markAsReadNotifications]);

  const notificationClassName = `push-notifications__content ${
    pushNotifications && pushNotifications.length ? '' : 'push-notifications__content_full-height'
  }`;

  return (
    <CustomDrawer isOpen={isOpenPushNotifications} handleClose={handleCloseDrawer} size="notifications">
      <div className="push-notifications" ref={containerRef}>
        <div className="push-notifications__header">
          <h3>{t('pushes.notifications')}</h3>
          <div className="push-notifications__header__close-button">
            <IconButton onClick={handleCloseDrawer} aria-label="close" size="small">
              <CloseIcon />
            </IconButton>
          </div>
        </div>
        <div className={notificationClassName} ref={notificationsParent}>
          {isNotificationsLoading && loadingPage ? (
            <>
              <NotificationSkeleton />
              <NotificationSkeleton />
              <NotificationSkeleton />
            </>
          ) : (
            <>
              {pushNotifications && pushNotifications.length ? (
                validateCreateNotificationDate([...pushNotifications].reverse()).map((item) => {
                  return (
                    <Notification
                      key={item.id}
                      setSeenNotificationsId={setSeenNotificationsId}
                      seenNotificationsId={seenNotificationsId}
                      firstUnreadNotificationId={findFirstUnreadNotification(pushNotifications)?.id || 0}
                      {...item}
                    />
                  );
                })
              ) : (
                <div className="push-notifications__content_empty">
                  <img src={BellIcon} alt="bell" />
                  <p>{t('pushes.youDontHaveNotifications')}</p>
                </div>
              )}
            </>
          )}
        </div>
        <div ref={bottomRef} />
      </div>
    </CustomDrawer>
  );
};

PushNotifications.propTypes = {
  pushNotifications: PropTypes.array,
  isNotificationsLoading: PropTypes.bool.isRequired,
  nextPageUrl: PropTypes.string,
  getMessages: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  setIsNewMessages: PropTypes.func.isRequired
};

export default PushNotifications;
